'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { detectPerformanceWarnings, warningToActivityEvent } from '@/lib/supabase/services/performanceWarningService';

interface ActivityEvent {
  id: string;
  icon: string;
  colorClass: string;
  message: string;
  timestamp: Date;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function formatAbsoluteTime(date: Date): string {
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

const EVENT_CONFIGS: Record<
  string,
  Record<string, { icon: string; color: string; message: (row: any) => string }>
> = {
  crew_members: {
    INSERT: {
      icon: '👤',
      color: 'bg-emerald-100 text-emerald-700',
      message: (r) => `New ${r.role ?? 'crew'} added: ${r.name ?? 'Unknown'} (${r.crew_code ?? ''})`,
    },
    UPDATE: {
      icon: '✏️',
      color: 'bg-blue-100 text-blue-700',
      message: (r) =>
        r.status
          ? `Crew status updated: ${r.name ?? 'Unknown'} → ${r.status}`
          : `Crew record updated: ${r.name ?? 'Unknown'}`,
    },
    DELETE: {
      icon: '🗑️',
      color: 'bg-red-100 text-red-700',
      message: (r) => `Crew member removed: ${r.name ?? r.crew_code ?? 'Unknown'}`,
    },
  },
  duty_assignments: {
    INSERT: {
      icon: '📋',
      color: 'bg-violet-100 text-violet-700',
      message: (r) =>
        `Duty assigned: ${r.duty_code ?? ''} — ${r.driver1_name ?? '—'} on ${r.route ?? ''}`,
    },
    UPDATE: {
      icon: '🔄',
      color: 'bg-amber-100 text-amber-700',
      message: (r) =>
        r.status === 'Completed'
          ? `Duty completed: ${r.duty_code ?? ''} by ${r.driver1_name ?? '—'}`
          : r.status === 'Active'
          ? `Duty started: ${r.duty_code ?? ''} — ${r.driver1_name ?? '—'}`
          : r.status === 'Cancelled'
          ? `Duty cancelled: ${r.duty_code ?? ''}`
          : `Duty updated: ${r.duty_code ?? ''} → ${r.status ?? 'Updated'}`,
    },
    DELETE: {
      icon: '❌',
      color: 'bg-red-100 text-red-700',
      message: (r) => `Duty removed: ${r.duty_code ?? 'Unknown'}`,
    },
  },
  accidents: {
    INSERT: {
      icon: '🚨',
      color: 'bg-red-100 text-red-700',
      message: (r) =>
        `${r.incident_type ?? 'Incident'} reported: ${r.accident_code ?? ''} — ${r.driver_name ?? '—'} on ${r.route ?? '—'}`,
    },
    UPDATE: {
      icon: '📝',
      color: 'bg-orange-100 text-orange-700',
      message: (r) =>
        r.status
          ? `Incident status changed: ${r.accident_code ?? ''} → ${r.status}`
          : `Incident updated: ${r.accident_code ?? ''}`,
    },
    DELETE: {
      icon: '🗑️',
      color: 'bg-gray-100 text-gray-600',
      message: (r) => `Incident record deleted: ${r.accident_code ?? 'Unknown'}`,
    },
  },
  violations: {
    INSERT: {
      icon: '⚠️',
      color: 'bg-yellow-100 text-yellow-700',
      message: (r) =>
        `Violation logged: ${r.crew_name ?? '—'} — ${r.category ?? 'Unknown'} (${r.violation_code ?? ''})`,
    },
    UPDATE: {
      icon: '💰',
      color: 'bg-orange-100 text-orange-700',
      message: (r) =>
        r.status
          ? `Violation status: ${r.violation_code ?? ''} → ${r.status}`
          : `Violation updated: ${r.violation_code ?? ''}`,
    },
    DELETE: {
      icon: '🗑️',
      color: 'bg-gray-100 text-gray-600',
      message: (r) => `Violation removed: ${r.violation_code ?? 'Unknown'}`,
    },
  },
  leave_applications: {
    INSERT: {
      icon: '🏖️',
      color: 'bg-sky-100 text-sky-700',
      message: (r) =>
        `Leave applied: ${r.crew_name ?? '—'} — ${r.leave_type ?? ''} (${r.total_days ?? 1} day${(r.total_days ?? 1) > 1 ? 's' : ''})`,
    },
    UPDATE: {
      icon: '✅',
      color: 'bg-teal-100 text-teal-700',
      message: (r) =>
        r.status === 'Approved'
          ? `Leave approved: ${r.crew_name ?? '—'} (${r.application_no ?? ''})`
          : r.status === 'Rejected'
          ? `Leave rejected: ${r.crew_name ?? '—'} (${r.application_no ?? ''})`
          : `Leave updated: ${r.crew_name ?? '—'} → ${r.status ?? 'Updated'}`,
    },
    DELETE: {
      icon: '🗑️',
      color: 'bg-gray-100 text-gray-600',
      message: (r) => `Leave application removed: ${r.application_no ?? 'Unknown'}`,
    },
  },
  bhatta_trips: {
    INSERT: {
      icon: '💵',
      color: 'bg-green-100 text-green-700',
      message: (r) =>
        `Bhatta trip added: ${r.driver_name ?? '—'} — ${r.route ?? ''} (${r.trip_code ?? ''})`,
    },
    UPDATE: {
      icon: '💳',
      color: 'bg-indigo-100 text-indigo-700',
      message: (r) =>
        r.bhatta_status
          ? `Bhatta status updated: ${r.trip_code ?? ''} → ${r.bhatta_status}`
          : `Bhatta trip updated: ${r.trip_code ?? ''}`,
    },
    DELETE: {
      icon: '🗑️',
      color: 'bg-gray-100 text-gray-600',
      message: (r) => `Bhatta trip removed: ${r.trip_code ?? 'Unknown'}`,
    },
  },
};

const TABLES = ['crew_members', 'duty_assignments', 'accidents', 'violations', 'leave_applications', 'bhatta_trips'] as const;
const MAX_EVENTS = 30;

export default function ActivityFeed() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const channelsRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']>[]>([]);

  // Tick every 30s to refresh relative timestamps
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const supabase = createClient();

    // Seed with recent records from each table on mount
    const seedInitialEvents = async () => {
      const seedEvents: ActivityEvent[] = [];

      // --- Performance warnings injected first (highest priority) ---
      try {
        const warnings = await detectPerformanceWarnings();
        warnings.forEach((w) => {
          const evt = warningToActivityEvent(w);
          seedEvents.push(evt);
        });
      } catch (err) {
        console.error('ActivityFeed: failed to load performance warnings', err);
      }

      // Recent crew changes
      const { data: crew } = await supabase
        .from('crew_members')
        .select('id, name, crew_code, role, status, created_at, updated_at')
        .order('created_at', { ascending: false })
        .limit(3);
      (crew ?? []).forEach((r) => {
        seedEvents.push({
          id: `crew-seed-${r.id}`,
          icon: '👤',
          colorClass: 'bg-emerald-100 text-emerald-700',
          message: `${r.role ?? 'Crew'} ${r.name} (${r.crew_code}) — ${r.status}`,
          timestamp: new Date(r.created_at),
        });
      });

      // Recent duties
      const { data: duties } = await supabase
        .from('duty_assignments')
        .select('id, duty_code, driver1_name, route, status, updated_at')
        .order('updated_at', { ascending: false })
        .limit(3);
      (duties ?? []).forEach((r) => {
        seedEvents.push({
          id: `duty-seed-${r.id}`,
          icon: '📋',
          colorClass: 'bg-violet-100 text-violet-700',
          message: `Duty ${r.duty_code} — ${r.driver1_name ?? '—'} on ${r.route ?? '—'} (${r.status})`,
          timestamp: new Date(r.updated_at),
        });
      });

      // Recent accidents
      const { data: accidents } = await supabase
        .from('accidents')
        .select('id, accident_code, incident_type, driver_name, route, status, created_at')
        .order('created_at', { ascending: false })
        .limit(2);
      (accidents ?? []).forEach((r) => {
        seedEvents.push({
          id: `acc-seed-${r.id}`,
          icon: '🚨',
          colorClass: 'bg-red-100 text-red-700',
          message: `${r.incident_type ?? 'Incident'} ${r.accident_code} — ${r.driver_name ?? '—'} on ${r.route ?? '—'} (${r.status})`,
          timestamp: new Date(r.created_at),
        });
      });

      // Recent violations
      const { data: violations } = await supabase
        .from('violations')
        .select('id, violation_code, crew_name, category, status, created_at')
        .order('created_at', { ascending: false })
        .limit(2);
      (violations ?? []).forEach((r) => {
        seedEvents.push({
          id: `vio-seed-${r.id}`,
          icon: '⚠️',
          colorClass: 'bg-yellow-100 text-yellow-700',
          message: `Violation ${r.violation_code} — ${r.crew_name ?? '—'} · ${r.category ?? 'Unknown'} (${r.status})`,
          timestamp: new Date(r.created_at),
        });
      });

      // Recent bhatta trips
      const { data: bhattaTrips } = await supabase
        .from('bhatta_trips')
        .select('id, trip_code, driver_name, route, bhatta_status, created_at')
        .order('created_at', { ascending: false })
        .limit(2);
      (bhattaTrips ?? []).forEach((r) => {
        seedEvents.push({
          id: `bhatta-seed-${r.id}`,
          icon: '💵',
          colorClass: 'bg-green-100 text-green-700',
          message: `Bhatta trip ${r.trip_code ?? ''} — ${r.driver_name ?? '—'} on ${r.route ?? ''} (${r.bhatta_status ?? 'Pending'})`,
          timestamp: new Date(r.created_at),
        });
      });

      // Sort all seed events newest first
      seedEvents.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setEvents(seedEvents.slice(0, MAX_EVENTS));
      setLoading(false);
    };

    seedInitialEvents();

    // Subscribe to real-time changes on all tables
    TABLES.forEach((table) => {
      const channel = supabase
        .channel(`activity_feed_${table}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table },
          (payload) => {
            const eventType = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
            const row = payload.new && Object.keys(payload.new).length > 0 ? payload.new : payload.old;
            const config = EVENT_CONFIGS[table]?.[eventType];
            if (!config || !row) return;

            const newEvent: ActivityEvent = {
              id: `${table}-${eventType}-${Date.now()}-${Math.random()}`,
              icon: config.icon,
              colorClass: config.color,
              message: config.message(row),
              timestamp: new Date(),
            };

            setEvents((prev) => [newEvent, ...prev].slice(0, MAX_EVENTS));
          }
        )
        .subscribe();
      channelsRef.current.push(channel);
    });

    return () => {
      channelsRef.current.forEach((ch) => supabase.removeChannel(ch));
      channelsRef.current = [];
    };
  }, []);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden h-full flex flex-col">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-base font-600 text-foreground">Recent Activity</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Live events · Auto-refreshing</p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </span>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {loading ? (
          <div className="flex flex-col gap-3 px-5 py-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-lg bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-muted rounded w-4/5" />
                  <div className="h-2.5 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-5 text-center">
            <span className="text-3xl mb-2">📭</span>
            <p className="text-sm text-muted-foreground">No activity yet</p>
            <p className="text-xs text-muted-foreground mt-1">Events will appear here as data changes</p>
          </div>
        ) : (
          events.map((evt) => (
            <div
              key={evt.id}
              className="flex gap-3 px-5 py-3.5 hover:bg-muted/50 transition-colors duration-100"
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-sm ${evt.colorClass}`}
              >
                {evt.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground leading-snug">{evt.message}</p>
                <p
                  className="text-xs text-muted-foreground mt-0.5 tabular-nums"
                  title={formatAbsoluteTime(evt.timestamp)}
                >
                  {formatRelativeTime(evt.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="px-5 py-3 border-t border-border shrink-0">
        <p className="text-xs text-muted-foreground">
          Showing {events.length} event{events.length !== 1 ? 's' : ''} · Performance Warnings, Crew, Duties, Incidents, Violations, Leave, Bhatta
        </p>
      </div>
    </div>
  );
}